import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Pencil, ArrowUp, ArrowDown, Check, X, Hammer } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const TradesManager = () => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/trades");
      setTrades(data.trades);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = adding.trim();
    if (!name) return;
    setBusy("add");
    try {
      await api.post("/admin/trades", { name });
      setAdding("");
      toast.success(`“${name}” added`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const rename = async (t) => {
    const name = draft.trim();
    if (!name || name === t.name) return setEditing(null);
    setBusy(t.name);
    try {
      const { data } = await api.patch("/admin/trades", { name: t.name, rename_to: name });
      toast.success(`Renamed to “${name}”`, {
        description: data.vendors_updated
          ? `${data.vendors_updated} vendor${data.vendors_updated === 1 ? "" : "s"} moved across automatically`
          : "No vendors were using the old name",
      });
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const remove = async (t) => {
    const warn = t.vendors
      ? `${t.vendors} vendor${t.vendors === 1 ? " is" : "s are"} listed under “${t.name}”. Removing it will untag them and they will disappear from this trade's search results.\n\nRemove it anyway?`
      : `Remove “${t.name}”? No vendors are using it.`;
    if (!window.confirm(warn)) return;
    setBusy(t.name);
    try {
      const { data } = await api.delete(`/admin/trades?name=${encodeURIComponent(t.name)}`);
      toast.success(`“${t.name}” removed`, {
        description: data.vendors_updated ? `${data.vendors_updated} vendor(s) untagged` : undefined,
      });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const move = async (i, dir) => {
    const next = [...trades];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setTrades(next);
    try {
      await api.put("/admin/trades", { items: next.map((t) => t.name) });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      load();
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-trades" className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
          <Hammer className="w-5 h-5 text-primary" />
        </div>
        <div>
          <div className="font-semibold text-slate-900 dark:text-slate-100">Trade list</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Drives vendor profiles and the marketplace filters. Renaming moves tagged vendors across
            automatically; the order here is the order buyers see.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <Input
          data-testid="new-trade-input"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add a trade — e.g. Solar & Renewables"
          className="h-10"
        />
        <Button data-testid="add-trade-button" onClick={add} disabled={busy === "add" || !adding.trim()} className="h-10 gap-2 font-semibold shrink-0">
          {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </Button>
      </div>

      <div data-testid="trade-list" className="divide-y divide-border">
        {trades.map((t, i) => (
          <div key={t.name} data-testid={`trade-row-${i}`} className="flex items-center gap-3 py-2.5">
            <span className="text-xs font-mono text-muted-foreground w-6 shrink-0">{i + 1}</span>
            {editing === t.name ? (
              <>
                <Input
                  data-testid={`trade-edit-input-${i}`}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); rename(t); } }}
                  className="h-9 flex-1"
                  autoFocus
                />
                <Button size="sm" data-testid={`trade-save-${i}`} onClick={() => rename(t)} disabled={busy === t.name} className="h-9 gap-1.5">
                  {busy === t.name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-9">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1 font-medium text-sm truncate">{t.name}</span>
                <span data-testid={`trade-count-${i}`} className={`px-2 py-0.5 rounded-md text-[11px] font-bold shrink-0 ${t.vendors ? "bg-brand-soft text-brand" : "bg-secondary text-muted-foreground"}`}>
                  {t.vendors} vendor{t.vendors === 1 ? "" : "s"}
                </span>
                <div className="flex items-center shrink-0">
                  <Button size="icon" variant="ghost" data-testid={`trade-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="h-8 w-8">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" data-testid={`trade-down-${i}`} onClick={() => move(i, 1)} disabled={i === trades.length - 1} className="h-8 w-8">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" data-testid={`trade-rename-${i}`} onClick={() => { setEditing(t.name); setDraft(t.name); }} className="h-8 w-8">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" data-testid={`trade-delete-${i}`} onClick={() => remove(t)} disabled={busy === t.name} className="h-8 w-8 text-muted-foreground hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
